import { Component } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { ActionConfig } from '../table-action-configs';

export interface ActionCellRendererParams extends ICellRendererParams {
  actions?: ActionConfig[];
  onActionClick?: (action: string, rowData: any) => void;
}

@Component({
  selector: 'app-custom-action-cell-renderer',
  template: `
   <div
      class="action-buttons"
      [class.compact]="isCompactMode()"
      [class.icon-only]="isIconOnlyMode()"
    >
      <app-button-group
        *ngFor="let actionConfig of visibleActions"
        [buttonType]="'Light'"
        [label]="actionConfig.label"
        [size]="'Small'"
        [icon]="actionConfig.icon || ''"
        [iconFillType]="'outlined'"
        [disabled]="isActionDisabled(actionConfig)"
        [class]="getButtonClass(actionConfig)"
        (click)="onActionClicked(actionConfig.action, actionConfig)"
      ></app-button-group>
    </div>
  `,
  styleUrls: ['./custom-action-cell-renderer.component.scss'],
})
export class CustomActionCellRendererComponent
  implements ICellRendererAngularComp
{
  public params!: ActionCellRendererParams;
  public visibleActions: ActionConfig[] = [];

  agInit(params: ActionCellRendererParams): void {
    this.params = params;
    this.updateVisibleActions();
  }

  refresh(params: ActionCellRendererParams): boolean {
    this.params = params;
    this.updateVisibleActions();
    return true;
  }

  private updateVisibleActions(): void {
    if (!this.params.actions) {
      this.visibleActions = [
        {
          action: 'view',
          label: 'View',
          icon: 'material-icons',
          tooltip: 'View details',
          cssClass: 'btn-view',
        },
        {
          action: 'edit',
          label: 'Edit',
          icon: 'material-icons',
          tooltip: 'Edit record',
          cssClass: 'btn-edit',
        },
        {
          action: 'delete',
          label: 'Delete',
          icon: 'material-icons',
          tooltip: 'Delete record',
          cssClass: 'btn-delete',
        },
      ];
    } else {
      this.visibleActions = this.params.actions.filter((action) =>
        this.isActionVisible(action),
      );
    }
  }

  private isActionVisible(action: ActionConfig): boolean {
    if (typeof action.visible === 'boolean') {
      return action.visible;
    }
    if (typeof action.visible === 'function') {
      return action.visible(this.params.data);
    }
    return true;
  }

  public isActionDisabled(action: ActionConfig): boolean {
    if (typeof action.disabled === 'boolean') {
      return action.disabled;
    }
    if (typeof action.disabled === 'function') {
      return action.disabled(this.params.data);
    }
    return false;
  }

  public getButtonClass(action: ActionConfig): string {
    const baseClasses = ['action-btn'];
    if (action.cssClass) {
      baseClasses.push(action.cssClass);
    }
    return baseClasses.join(' ');
  }

  public onActionClicked(action: string, actionConfig: ActionConfig): void {
    if (this.isActionDisabled(actionConfig)) {
      return;
    }
    if (this.params.onActionClick) {
      this.params.onActionClick(action, this.params.data);
    } else if (this.params?.context?.componentParent) {
      this.params.context.componentParent.onActionClick(
        action,
        this.params.data,
      );
    }
  }

  public showLabel(action: ActionConfig): boolean {
    return !this.isIconOnlyMode();
  }

  public isCompactMode(): boolean {
    return this.visibleActions.length >= 3 && this.visibleActions.length <= 4;
  }

  public isIconOnlyMode(): boolean {
    return this.visibleActions.length >= 5;
  }
}
